import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { get, map } from 'lodash'

import * as formatters from 'components/list/formatters/listFormatters'

import { ReactComponent as ArrowIcon } from 'assets/icons/arrow-selected-item.svg'

const DEFAULT_FORMATTER_NAME = 'formatDefault'
const DEFAULT_ROW_TITLE_FORMATTER_NAME = 'rowTitleFormatDefault'

const rowRendererHelper = ({ index, key, parent, style, item, rowRenderer, isRowSelected, onRowClick }) => {
  const isSelected = isRowSelected({ item })
  const rowProps = {}

  if (onRowClick) {
    rowProps.onClick = event => onRowClick({ event, item })
  }

  const content = (
    <Fragment>
      {rowRenderer({
        index,
        key,
        parent,
        style,
        item,
      })}

      {item && isSelected && (
        <div className="selected-item-icon">
          <ArrowIcon width="16" height="16" className="arrow-icon" />
        </div>
      )}
    </Fragment>
  )

  return {
    content,
    isSelected,
    rowProps,
  }
}

rowRendererHelper.propTypes = {
  item: PropTypes.array.isRequired,
  rowRenderer: PropTypes.func.isRequired,
  isRowSelected: PropTypes.func,
  onRowClick: PropTypes.func,
  index: PropTypes.number,
}

const groupedRowRendererHelper = ({
  index,
  key,
  parent,
  item = {},
  rowRenderer,
  isRowSelected,
  onRowClick,
  rowTitleFormatterKey = DEFAULT_ROW_TITLE_FORMATTER_NAME,
  rowTitleRenderer = formatters[rowTitleFormatterKey],
}) => {
  const { items } = item
  const rowTitle = rowTitleRenderer({ item })

  const subRows = map(items, subItem => {
    const isSelected = isRowSelected({ item: subItem })
    const props = {}

    if (onRowClick) {
      props.onClick = event => onRowClick({ event, item: subItem })
    }

    return (
      <div className="list-item grouped-list-item" key={get(subItem, 'id')} {...props}>
        {rowRenderer({
          index,
          key,
          parent,
          item: subItem,
        })}

        {subItem && isSelected && (
          <div className="selected-item-icon">
            <ArrowIcon width="16" height="16" className="arrow-icon" />
          </div>
        )}
      </div>
    )
  })

  return (
    <Fragment>
      <div className="group-title">{rowTitle}</div>

      {subRows}
    </Fragment>
  )
}

groupedRowRendererHelper.propTypes = {
  index: PropTypes.number,
  key: PropTypes.string,
  parent: PropTypes.node,
  item: PropTypes.array.isRequired,
  rowRenderer: PropTypes.func.isRequired,
  isRowSelected: PropTypes.func,
  onRowClick: PropTypes.func,
  rowTitleFormatterKey: PropTypes.func,
  rowTitleRenderer: PropTypes.func,
}

export const rowContainerRendererHandler =
  ({
    loadingRowValue = (
      <div className="default-loading-row">
        <div className="loader" />
      </div>
    ),
    list,
    rowDataKey,
    formatterKey = DEFAULT_FORMATTER_NAME,
    rowRenderer = formatters[formatterKey],
    isRowSelected = () => false,
    onRowClick,
    isGrouped,
    rowTitleRenderer,
    noRowGutter = false,
  }) =>
  // eslint-disable-next-line
  ({ index, key, parent, style }) => {
    const item = rowDataKey ? get(list, `[${index}][${rowDataKey}]`) : get(list, `[${index}]`)
    const wrapperClassNames = {}

    let wrapperContent

    let wrapperProps = {}

    if (isGrouped) {
      wrapperContent = groupedRowRendererHelper({
        item,
        index,
        rowRenderer,
        isRowSelected,
        onRowClick,
        rowTitleRenderer,
      })
    } else {
      const { content, isSelected, rowProps } = rowRendererHelper({
        index,
        key,
        parent,
        style,
        item,
        rowRenderer,
        isRowSelected,
        onRowClick,
      })

      wrapperClassNames.selected = isSelected
      wrapperProps = rowProps
      wrapperContent = content
    }

    wrapperContent = item ? wrapperContent : loadingRowValue

    return (
      <div
        key={key}
        style={style}
        className={classNames('list-item-wrapper', wrapperClassNames, { 'no-gutter': noRowGutter })}
        {...wrapperProps}>
        {wrapperContent}
      </div>
    )
  }

rowContainerRendererHandler.propTypes = {
  loadingRowValue: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  list: PropTypes.array.isRequired,
  rowDataKey: PropTypes.string.isRequired,
  rowRenderer: PropTypes.func.isRequired,
  isRowSelected: PropTypes.func,
  onRowClick: PropTypes.func,
  noRowGutter: PropTypes.bool,
}
